import React from "react";
import { useEditor } from "@/lib/store";
import { OBJECT_DEFS } from "@/lib/objectDefs";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { Trash2, Copy, RotateCw, Lock, LockOpen, Rows3, DoorOpen, Plus } from "lucide-react";

// Colours + labels for each door kind — reused by 2D / 3D renderers via
// the `data-door-kind` attribute + this map for the properties panel UI.
export const DOOR_KINDS = {
  entry:     { label: "Entry",       color: "#22c55e" },
  exit:      { label: "Exit",        color: "#eab308" },
  fire_exit: { label: "Fire Exit",   color: "#ef4444" },
  loading:   { label: "Loading Dock", color: "#06b6d4" },
};

// -------- Visual stair-position picker --------
// Renders a small SVG rectangle representing the mezzanine, with 8 clickable
// dots around it. Clicking a dot chooses that stair position — much more
// intuitive than a dropdown of `front_left / back_right / ...` strings.
const STAIR_SPOTS = [
  { id: "front_left",   x: 12,  y: 52, label: "FL" },
  { id: "front_center", x: 60,  y: 52, label: "FC" },
  { id: "front_right",  x: 108, y: 52, label: "FR" },
  { id: "back_left",    x: 12,  y: 12, label: "BL" },
  { id: "back_center",  x: 60,  y: 12, label: "BC" },
  { id: "back_right",   x: 108, y: 12, label: "BR" },
  { id: "side_left",    x: 12,  y: 32, label: "SL" },
  { id: "side_right",   x: 108, y: 32, label: "SR" },
];

function StairPicker({ value, onChange }) {
  return (
    <div className="rounded-sm border border-border/60 bg-background/30 p-2">
      <svg viewBox="0 0 120 64" className="w-full h-auto">
        {/* mezzanine slab (front is bottom) */}
        <rect x={20} y={16} width={80} height={32} rx={2} fill="#eab30833" stroke="#eab308" strokeWidth="1" />
        <text x={60} y={34} fill="#94a3b8" fontSize="7" fontFamily="JetBrains Mono, monospace" textAnchor="middle" dominantBaseline="middle">
          mezzanine
        </text>
        {/* front/back edge labels */}
        <text x={60} y={57} fill="#64748b" fontSize="5" fontFamily="JetBrains Mono, monospace" textAnchor="middle">front</text>
        <text x={60} y={9}  fill="#64748b" fontSize="5" fontFamily="JetBrains Mono, monospace" textAnchor="middle">back</text>

        {STAIR_SPOTS.map((s) => {
          const active = s.id === value;
          return (
            <g key={s.id} data-testid={`stair-spot-${s.id}`} onClick={() => onChange(s.id)} style={{ cursor: "pointer" }}>
              <circle cx={s.x} cy={s.y} r={5} fill={active ? "hsl(16 100% 56%)" : "#f5f5f5"} stroke={active ? "hsl(16 100% 56%)" : "#94a3b8"} strokeWidth="1" />
              <text x={s.x} y={s.y + 0.3} fill={active ? "#fff" : "#475569"} fontSize="4" fontFamily="JetBrains Mono, monospace" textAnchor="middle" dominantBaseline="middle" fontWeight="700" pointerEvents="none">
                {s.label}
              </text>
            </g>
          );
        })}
      </svg>
      <div className="font-mono text-[10px] text-muted-foreground mt-1 text-center capitalize">
        {(value || "front_left").replaceAll("_", " ")}
      </div>
    </div>
  );
}

function FloorProps() {
  const floor = useEditor((s) => s.floor);
  const setFloor = useEditor((s) => s.setFloor);
  const setFloorShape = useEditor((s) => s.setFloorShape);
  const setWallHeight = useEditor((s) => s.setWallHeight);
  const setWallLength = useEditor((s) => s.setWallLength);
  const setBoundingSize = useEditor((s) => s.setBoundingSize);
  const projectName = useEditor((s) => s.projectName);
  const setProjectName = useEditor((s) => s.setProjectName);
  const clientName = useEditor((s) => s.clientName);
  const setClientName = useEditor((s) => s.setClientName);

  // Compute area + perimeter using shoelace + bounding-box for dimensions
  const verts = floor.vertices || [];
  let area = 0;
  let perim = 0;
  const xs = verts.map((v) => v.x);
  const ys = verts.map((v) => v.y);
  const bboxL = xs.length ? Math.max(...xs) - Math.min(...xs) : 0;
  const bboxW = ys.length ? Math.max(...ys) - Math.min(...ys) : 0;
  for (let i = 0; i < verts.length; i++) {
    const a = verts[i];
    const b = verts[(i + 1) % verts.length];
    area += a.x * b.y - b.x * a.y;
    perim += Math.hypot(b.x - a.x, b.y - a.y);
  }
  area = Math.abs(area) / 2; // mm²

  return (
    <div className="space-y-4 p-3">
      <div>
        <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-muted-foreground mb-2">// project</div>
        <div className="space-y-2">
          <div className="space-y-1">
            <Label className="text-[11px] text-muted-foreground">Name</Label>
            <Input
              data-testid="floor-project-name"
              className="h-8 text-xs rounded-sm"
              value={projectName}
              onChange={(e) => setProjectName(e.target.value)}
            />
          </div>
          <div className="space-y-1">
            <Label className="text-[11px] text-muted-foreground">Client</Label>
            <Input
              data-testid="floor-client-name"
              className="h-8 text-xs rounded-sm"
              value={clientName}
              onChange={(e) => setClientName(e.target.value)}
            />
          </div>
        </div>
      </div>

      <div>
        <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-muted-foreground mb-2">// floor plan</div>
        <div className="space-y-2.5">
          <div className="space-y-1">
            <Label className="text-[11px] text-muted-foreground">Shape</Label>
            <Select value={floor.shape} onValueChange={setFloorShape}>
              <SelectTrigger data-testid="floor-shape-select" className="h-8 text-xs rounded-sm"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="rectangle">Rectangle</SelectItem>
                <SelectItem value="l_shape">L-Shape</SelectItem>
                <SelectItem value="polygon">Custom Polygon</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Explicit warehouse dimensions */}
          <div className="grid grid-cols-2 gap-2">
            <div className="space-y-1">
              <Label className="text-[10px] text-muted-foreground flex items-center justify-between">
                <span>Length (X)</span>
                <span className="font-mono text-[9px]">mm</span>
              </Label>
              <Input
                data-testid="floor-length"
                type="number"
                min={500}
                step={100}
                className="h-8 text-xs rounded-sm font-mono"
                value={Math.round(bboxL)}
                onChange={(e) => setBoundingSize({ length: Number(e.target.value) || bboxL, width: bboxW })}
              />
            </div>
            <div className="space-y-1">
              <Label className="text-[10px] text-muted-foreground flex items-center justify-between">
                <span>Width (Y)</span>
                <span className="font-mono text-[9px]">mm</span>
              </Label>
              <Input
                data-testid="floor-width"
                type="number"
                min={500}
                step={100}
                className="h-8 text-xs rounded-sm font-mono"
                value={Math.round(bboxW)}
                onChange={(e) => setBoundingSize({ length: bboxL, width: Number(e.target.value) || bboxW })}
              />
            </div>
          </div>

          <div className="space-y-1">
            <Label className="text-[11px] text-muted-foreground">Wall Height (mm)</Label>
            <Input
              data-testid="floor-wall-height"
              className="h-8 text-xs rounded-sm font-mono"
              type="number"
              value={floor.wall_height}
              onChange={(e) => setWallHeight(Number(e.target.value) || 0)}
            />
          </div>

          <div className="grid grid-cols-2 gap-2 pt-1">
            <div className="bg-secondary/40 rounded-sm p-2">
              <div className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">Area</div>
              <div className="font-mono text-sm">{(area / 1_000_000).toFixed(1)} m²</div>
            </div>
            <div className="bg-secondary/40 rounded-sm p-2">
              <div className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">Perim.</div>
              <div className="font-mono text-sm">{(perim / 1000).toFixed(1)} m</div>
            </div>
          </div>
        </div>
      </div>

      {verts.length > 0 && verts.length <= 12 && (
        <div>
          <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-muted-foreground mb-2">// walls</div>
          <div className="space-y-1.5 max-h-56 overflow-y-auto scrollbar-thin pr-1">
            {verts.map((a, i) => {
              const b = verts[(i + 1) % verts.length];
              const len = Math.hypot(b.x - a.x, b.y - a.y);
              return (
                <div key={i} className="flex items-center gap-1.5">
                  <span className="font-mono text-[10px] text-muted-foreground w-6">W{i + 1}</span>
                  <Input
                    data-testid={`wall-length-${i}`}
                    type="number"
                    className="h-7 text-xs rounded-sm font-mono"
                    value={Math.round(len)}
                    onChange={(e) => setWallLength(i, Number(e.target.value) || len)}
                  />
                  <span className="font-mono text-[10px] text-muted-foreground">mm</span>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Finishes — colour + texture for interior walls & floor. Persisted on
          the floor object so it's saved with the project. */}
      <FinishControls floor={floor} setFloor={setFloor} />

      {/* Doors — first-class entities on walls. Each door lives on a wall edge
          at a given offset (mm from the edge start) and has a width/height. */}
      {verts.length >= 2 && <DoorList verts={verts} doors={floor.doors || []} />}
    </div>
  );
}

// -------- Wall / Floor finish sub-section --------
const WALL_FINISHES = [
  ["plain", "Plain"],
  ["brick", "Brick"],
  ["tile", "Tile"],
  ["wood", "Wood"],
  ["concrete", "Concrete"],
];
const FLOOR_FINISHES = [
  ["concrete", "Concrete"],
  ["epoxy", "Epoxy"],
  ["tile", "Tile"],
  ["wood", "Wood"],
  ["brick", "Brick"],
  ["plain", "Plain"],
];
function FinishRow({ label, finish, color, finishes, onChange, testidPrefix }) {
  return (
    <div className="rounded-sm border border-border/60 bg-background/30 p-2 space-y-2">
      <div className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">{label}</div>
      <div className="grid grid-cols-[1fr_auto] gap-1.5 items-center">
        <Select value={finish} onValueChange={(v) => onChange({ finish: v })}>
          <SelectTrigger data-testid={`${testidPrefix}-finish`} className="h-8 text-xs rounded-sm">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {finishes.map(([id, lbl]) => (
              <SelectItem key={id} value={id}>{lbl}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <input
          type="color"
          data-testid={`${testidPrefix}-color`}
          value={color}
          onChange={(e) => onChange({ color: e.target.value })}
          className="h-8 w-10 rounded-sm border border-border/60 bg-transparent cursor-pointer"
          title="Base tint"
        />
      </div>
    </div>
  );
}
function FinishControls({ floor, setFloor }) {
  const wallFinish  = floor.wall_finish  || "plain";
  const wallColor   = floor.wall_color   || "#e7dfd2";
  const floorFinish = floor.floor_finish || "concrete";
  const floorColor  = floor.floor_color  || "#c8c2b6";
  return (
    <div>
      <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-muted-foreground mb-2">// finishes</div>
      <div className="space-y-2">
        <FinishRow
          label="Walls"
          finish={wallFinish}
          color={wallColor}
          finishes={WALL_FINISHES}
          testidPrefix="wall"
          onChange={(p) => setFloor({
            wall_finish: p.finish ?? wallFinish,
            wall_color: p.color ?? wallColor,
          })}
        />
        <FinishRow
          label="Floor"
          finish={floorFinish}
          color={floorColor}
          finishes={FLOOR_FINISHES}
          testidPrefix="floor"
          onChange={(p) => setFloor({
            floor_finish: p.finish ?? floorFinish,
            floor_color:  p.color  ?? floorColor,
          })}
        />
      </div>
    </div>
  );
}

// -------- Doors sub-section --------
function DoorList({ verts, doors }) {
  const addDoor = useEditor((s) => s.addDoor);
  const updateDoor = useEditor((s) => s.updateDoor);
  const removeDoor = useEditor((s) => s.removeDoor);
  const [pendingKind, setPendingKind] = React.useState("entry");

  return (
    <div>
      <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-muted-foreground mb-2 flex items-center justify-between">
        <span>// doors ({doors.length})</span>
      </div>

      {/* Add-door row */}
      <div className="flex items-center gap-1.5 mb-2">
        <Select value={pendingKind} onValueChange={setPendingKind}>
          <SelectTrigger data-testid="door-kind-select" className="h-7 text-xs rounded-sm flex-1"><SelectValue /></SelectTrigger>
          <SelectContent>
            {Object.entries(DOOR_KINDS).map(([k, v]) => (
              <SelectItem key={k} value={k}>{v.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Button
          data-testid="door-add"
          size="sm"
          variant="secondary"
          className="h-7 rounded-sm font-mono text-[10px] uppercase tracking-widest px-2"
          onClick={() => addDoor(pendingKind, 0)}
        >
          <Plus className="w-3 h-3 mr-1" /> Add
        </Button>
      </div>

      {/* Existing doors */}
      {doors.length === 0 && (
        <div className="font-mono text-[10px] text-muted-foreground italic px-1 py-2">
          No doors yet — pick a kind above and add one to a wall.
        </div>
      )}
      <div className="space-y-2 max-h-64 overflow-y-auto scrollbar-thin pr-1">
        {doors.map((d) => {
          const meta = DOOR_KINDS[d.kind] || DOOR_KINDS.entry;
          const wallLen = (() => {
            const a = verts[d.edge_idx] || verts[0];
            const b = verts[(d.edge_idx + 1) % verts.length] || verts[1];
            return Math.hypot(b.x - a.x, b.y - a.y);
          })();
          return (
            <div
              key={d.id}
              data-testid={`door-row-${d.id}`}
              className="rounded-sm border border-border/60 bg-background/30 p-2 space-y-1.5"
            >
              <div className="flex items-center justify-between gap-1.5">
                <div className="flex items-center gap-1.5 min-w-0">
                  <span className="w-2 h-2 rounded-sm shrink-0" style={{ background: meta.color }} />
                  <DoorOpen className="w-3 h-3 text-muted-foreground shrink-0" />
                  <span className="font-mono text-[10px] uppercase tracking-widest text-foreground truncate">
                    {meta.label}
                  </span>
                </div>
                <Button
                  data-testid={`door-remove-${d.id}`}
                  size="icon"
                  variant="ghost"
                  className="h-6 w-6 rounded-sm text-muted-foreground hover:text-destructive"
                  onClick={() => removeDoor(d.id)}
                >
                  <Trash2 className="w-3 h-3" />
                </Button>
              </div>
              <div className="grid grid-cols-3 gap-1">
                <div className="space-y-0.5">
                  <Label className="text-[9px] text-muted-foreground uppercase tracking-widest">Wall</Label>
                  <Select
                    value={String(d.edge_idx)}
                    onValueChange={(v) => updateDoor(d.id, { edge_idx: Number(v) })}
                  >
                    <SelectTrigger data-testid={`door-edge-${d.id}`} className="h-6 text-[11px] rounded-sm">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {verts.map((_, i) => (
                        <SelectItem key={i} value={String(i)}>W{i + 1}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-0.5">
                  <Label className="text-[9px] text-muted-foreground uppercase tracking-widest">Offset</Label>
                  <Input
                    data-testid={`door-offset-${d.id}`}
                    type="number"
                    step={100}
                    min={0}
                    max={Math.max(0, Math.round(wallLen - d.width))}
                    className="h-6 text-[11px] rounded-sm font-mono"
                    value={d.offset}
                    onChange={(e) => updateDoor(d.id, { offset: Math.max(0, Math.min(wallLen - d.width, Number(e.target.value) || 0)) })}
                  />
                </div>
                <div className="space-y-0.5">
                  <Label className="text-[9px] text-muted-foreground uppercase tracking-widest">Width</Label>
                  <Input
                    data-testid={`door-width-${d.id}`}
                    type="number"
                    step={100}
                    min={600}
                    className="h-6 text-[11px] rounded-sm font-mono"
                    value={d.width}
                    onChange={(e) => updateDoor(d.id, { width: Math.max(600, Number(e.target.value) || 0) })}
                  />
                </div>
              </div>
              <div className="grid grid-cols-1 gap-1">
                <div className="space-y-0.5">
                  <Label className="text-[9px] text-muted-foreground uppercase tracking-widest">Height</Label>
                  <Input
                    data-testid={`door-height-${d.id}`}
                    type="number"
                    step={100}
                    min={800}
                    className="h-6 text-[11px] rounded-sm font-mono"
                    value={d.height}
                    onChange={(e) => updateDoor(d.id, { height: Math.max(800, Number(e.target.value) || 0) })}
                  />
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function ObjectProps({ obj }) {
  const def = OBJECT_DEFS[obj.type];
  const updateObject = useEditor((s) => s.updateObject);
  const updateObjectProps = useEditor((s) => s.updateObjectProps);
  const removeObject = useEditor((s) => s.removeObject);
  const duplicateObject = useEditor((s) => s.duplicateObject);
  const duplicateRow = useEditor((s) => s.duplicateRow);
  const rotateObject = useEditor((s) => s.rotateObject);
  const [rowCount, setRowCount] = React.useState(5);
  const [rowAxis, setRowAxis] = React.useState("length");
  const [rowGap, setRowGap] = React.useState(0);

  if (!def) return null;

  // Legacy scalar rotation → normalise to {x,y,z}
  const rot = typeof obj.rotation === "number"
    ? { x: 0, y: obj.rotation, z: 0 }
    : { x: 0, y: 0, z: 0, ...(obj.rotation || {}) };

  const setRotAxis = (axis, val) => {
    const next = { ...rot, [axis]: Number(val) || 0 };
    updateObject(obj.id, { rotation: next });
  };

  return (
    <div className="space-y-4 p-3">
      <div>
        <div className="flex items-center justify-between mb-2">
          <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-muted-foreground">// object</div>
          <div className="flex items-center gap-1">
            <Button
              data-testid="prop-rotate"
              size="icon"
              variant="ghost"
              className="h-7 w-7 rounded-sm"
              onClick={() => rotateObject(obj.id, 90, "y")}
              title="Rotate 90° around vertical"
            >
              <RotateCw className="w-3.5 h-3.5" />
            </Button>
            <Button
              data-testid="prop-duplicate"
              size="icon"
              variant="ghost"
              className="h-7 w-7 rounded-sm"
              onClick={() => duplicateObject(obj.id)}
              title="Duplicate"
            >
              <Copy className="w-3.5 h-3.5" />
            </Button>
            <Button
              data-testid="prop-lock"
              size="icon"
              variant="ghost"
              className="h-7 w-7 rounded-sm"
              onClick={() => updateObject(obj.id, { locked: !obj.locked })}
              title={obj.locked ? "Unlock" : "Lock"}
            >
              {obj.locked ? <Lock className="w-3.5 h-3.5" /> : <LockOpen className="w-3.5 h-3.5" />}
            </Button>
            <Button
              data-testid="prop-delete"
              size="icon"
              variant="ghost"
              className="h-7 w-7 rounded-sm text-destructive hover:text-destructive"
              onClick={() => removeObject(obj.id)}
            >
              <Trash2 className="w-3.5 h-3.5" />
            </Button>
          </div>
        </div>
        <div className="text-sm font-semibold tracking-tight">{def.label}</div>
        <div className="font-mono text-[10px] text-muted-foreground mt-0.5">id: {obj.id.slice(0, 12)}</div>
      </div>

      {/* Position + rotation */}
      <div>
        <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-muted-foreground mb-2">// transform</div>
        <div className="grid grid-cols-2 gap-1.5">
          <div className="space-y-0.5">
            <Label className="text-[10px] text-muted-foreground">Pos X</Label>
            <Input
              data-testid="param-pos-x"
              type="number" className="h-7 text-xs rounded-sm font-mono"
              value={Math.round(obj.position.x)}
              onChange={(e) => updateObject(obj.id, { position: { ...obj.position, x: Number(e.target.value) || 0 } })}
            />
          </div>
          <div className="space-y-0.5">
            <Label className="text-[10px] text-muted-foreground">Pos Y</Label>
            <Input
              data-testid="param-pos-y"
              type="number" className="h-7 text-xs rounded-sm font-mono"
              value={Math.round(obj.position.y)}
              onChange={(e) => updateObject(obj.id, { position: { ...obj.position, y: Number(e.target.value) || 0 } })}
            />
          </div>
        </div>
        <div className="grid grid-cols-3 gap-1.5 mt-2">
          <div className="space-y-0.5">
            <Label className="text-[10px] text-muted-foreground flex items-center justify-between">
              <span>Rot X°</span>
              <button
                data-testid="rot-x-quick"
                onClick={() => rotateObject(obj.id, 90, "x")}
                className="text-[9px] font-mono text-muted-foreground hover:text-primary transition-colors"
                title="Rotate 90°"
              >
                +90
              </button>
            </Label>
            <Input
              data-testid="param-rot-x"
              type="number" step={5} className="h-7 text-xs rounded-sm font-mono"
              value={Math.round(rot.x)}
              onChange={(e) => setRotAxis("x", e.target.value)}
            />
          </div>
          <div className="space-y-0.5">
            <Label className="text-[10px] text-muted-foreground flex items-center justify-between">
              <span>Rot Y°</span>
              <button
                data-testid="rot-y-quick"
                onClick={() => rotateObject(obj.id, 90, "y")}
                className="text-[9px] font-mono text-muted-foreground hover:text-primary transition-colors"
                title="Rotate 90°"
              >
                +90
              </button>
            </Label>
            <Input
              data-testid="param-rot-y"
              type="number" step={5} className="h-7 text-xs rounded-sm font-mono"
              value={Math.round(rot.y)}
              onChange={(e) => setRotAxis("y", e.target.value)}
            />
          </div>
          <div className="space-y-0.5">
            <Label className="text-[10px] text-muted-foreground flex items-center justify-between">
              <span>Rot Z°</span>
              <button
                data-testid="rot-z-quick"
                onClick={() => rotateObject(obj.id, 90, "z")}
                className="text-[9px] font-mono text-muted-foreground hover:text-primary transition-colors"
                title="Rotate 90°"
              >
                +90
              </button>
            </Label>
            <Input
              data-testid="param-rot-z"
              type="number" step={5} className="h-7 text-xs rounded-sm font-mono"
              value={Math.round(rot.z)}
              onChange={(e) => setRotAxis("z", e.target.value)}
            />
          </div>
        </div>
      </div>

      {/* Duplicate row — the highest-frequency action for laying out a rack
          aisle. Clones the selected object N-1 times along its long/short
          axis, spaced by its own footprint + optional gap. Rotation-aware. */}
      <div>
        <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-muted-foreground mb-2">// duplicate row</div>
        <div className="grid grid-cols-3 gap-1.5">
          <div className="space-y-0.5">
            <Label className="text-[10px] text-muted-foreground">Count</Label>
            <Input
              data-testid="row-count"
              type="number" min={2} max={50} step={1}
              className="h-7 text-xs rounded-sm font-mono"
              value={rowCount}
              onChange={(e) => setRowCount(Math.max(2, Math.min(50, Number(e.target.value) || 2)))}
            />
          </div>
          <div className="space-y-0.5">
            <Label className="text-[10px] text-muted-foreground">Axis</Label>
            <Select value={rowAxis} onValueChange={setRowAxis}>
              <SelectTrigger data-testid="row-axis" className="h-7 text-xs rounded-sm"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="length">Long side</SelectItem>
                <SelectItem value="width">Short side</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-0.5">
            <Label className="text-[10px] text-muted-foreground flex items-center justify-between">
              <span>Gap</span>
              <span className="font-mono text-[9px]">mm</span>
            </Label>
            <Input
              data-testid="row-gap"
              type="number" step={50}
              className="h-7 text-xs rounded-sm font-mono"
              value={rowGap}
              onChange={(e) => setRowGap(Number(e.target.value) || 0)}
            />
          </div>
        </div>
        <Button
          data-testid="row-apply"
          size="sm"
          variant="secondary"
          className="w-full mt-2 h-8 rounded-sm font-mono text-[11px] uppercase tracking-widest"
          onClick={() => duplicateRow(obj.id, rowCount, rowAxis, rowGap)}
          title="Clone this rack N times along the chosen axis (flush by default)"
        >
          <Rows3 className="w-3.5 h-3.5 mr-1.5" />
          Clone {rowCount}× row
        </Button>
      </div>

      {/* Schema-driven parametric inputs */}
      <div>
        <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-muted-foreground mb-2">// parameters</div>
        <div className="space-y-2">
          {def.schema.map((s) => {
            if (s.showIf && !s.showIf(obj.props)) return null;
            const val = obj.props[s.key];
            if (s.type === "boolean") {
              return (
                <div key={s.key} className="flex items-center justify-between py-0.5">
                  <Label className="text-xs">{s.label}</Label>
                  <Switch
                    data-testid={`param-${s.key}`}
                    checked={!!val}
                    onCheckedChange={(v) => updateObjectProps(obj.id, { [s.key]: v })}
                  />
                </div>
              );
            }
            if (s.type === "stair_picker") {
              return (
                <div key={s.key} className="space-y-1">
                  <Label className="text-[10px] text-muted-foreground">{s.label}</Label>
                  <StairPicker
                    value={val}
                    onChange={(v) => updateObjectProps(obj.id, { [s.key]: v })}
                  />
                </div>
              );
            }
            if (s.type === "select") {
              return (
                <div key={s.key} className="space-y-0.5">
                  <Label className="text-[10px] text-muted-foreground">{s.label}</Label>
                  <Select value={String(val)} onValueChange={(v) => updateObjectProps(obj.id, { [s.key]: v })}>
                    <SelectTrigger data-testid={`param-${s.key}`} className="h-7 text-xs rounded-sm"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {s.options.map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              );
            }
            if (s.type === "text") {
              return (
                <div key={s.key} className="space-y-0.5">
                  <Label className="text-[10px] text-muted-foreground">{s.label}</Label>
                  <Input
                    data-testid={`param-${s.key}`}
                    className="h-7 text-xs rounded-sm font-mono"
                    value={val ?? ""}
                    onChange={(e) => updateObjectProps(obj.id, { [s.key]: e.target.value })}
                  />
                </div>
              );
            }
            return (
              <div key={s.key} className="space-y-0.5">
                <Label className="text-[10px] text-muted-foreground flex items-center justify-between">
                  <span>{s.label}</span>
                  {s.unit && <span className="font-mono text-[9px]">{s.unit}</span>}
                </Label>
                <Input
                  data-testid={`param-${s.key}`}
                  type="number"
                  min={s.min}
                  max={s.max}
                  step={s.step}
                  className="h-7 text-xs rounded-sm font-mono"
                  value={val ?? 0}
                  onChange={(e) => updateObjectProps(obj.id, { [s.key]: Number(e.target.value) })}
                />
              </div>
            );
          })}
        </div>
      </div>

      {/* Color */}
      <div>
        <Label className="text-[10px] text-muted-foreground">Colour</Label>
        <div className="flex items-center gap-2 mt-1">
          <input
            type="color"
            value={obj.color}
            onChange={(e) => updateObject(obj.id, { color: e.target.value })}
            className="w-7 h-7 rounded-sm bg-transparent cursor-pointer border border-border"
          />
          <Input
            value={obj.color}
            onChange={(e) => updateObject(obj.id, { color: e.target.value })}
            className="h-7 text-xs rounded-sm font-mono flex-1"
          />
        </div>
      </div>
    </div>
  );
}

// -------- Obstacle inspector --------
function ObstacleProps({ ob }) {
  const updateObstacle = useEditor((s) => s.updateObstacle);
  const removeObstacle = useEditor((s) => s.removeObstacle);
  const setSelected = useEditor((s) => s.setSelected);
  const kindLabel = {
    pillar: "Pillar / Column",
    office_block: "Office Block",
    loading_dock: "Loading / Unloading Dock",
  }[ob.kind] || ob.kind;

  const patch = (p) => updateObstacle(ob.id, p);
  const dimField = (key, label, min, max) => (
    <div className="space-y-1" key={key}>
      <Label className="text-[11px] text-muted-foreground">{label} (mm)</Label>
      <Input
        data-testid={`obstacle-${key}`}
        type="number"
        className="h-8 text-xs rounded-sm font-mono"
        value={ob[key] ?? 0}
        min={min}
        max={max}
        step={50}
        onChange={(e) => patch({ [key]: Math.max(min, Number(e.target.value) || 0) })}
      />
    </div>
  );

  return (
    <div className="p-3 space-y-4">
      <div>
        <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-muted-foreground">// obstacle</div>
        <div className="text-base font-semibold tracking-tight mt-0.5">{kindLabel}</div>
        <div className="font-mono text-[10px] text-muted-foreground mt-0.5">id: {ob.id}</div>
      </div>

      <div>
        <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-muted-foreground mb-2">// position</div>
        <div className="grid grid-cols-2 gap-2">
          <div className="space-y-1">
            <Label className="text-[11px] text-muted-foreground">Pos X</Label>
            <Input
              data-testid="obstacle-x"
              type="number"
              className="h-8 text-xs rounded-sm font-mono"
              value={Math.round(ob.x)}
              onChange={(e) => patch({ x: Number(e.target.value) || 0 })}
            />
          </div>
          <div className="space-y-1">
            <Label className="text-[11px] text-muted-foreground">Pos Y</Label>
            <Input
              data-testid="obstacle-y"
              type="number"
              className="h-8 text-xs rounded-sm font-mono"
              value={Math.round(ob.y)}
              onChange={(e) => patch({ y: Number(e.target.value) || 0 })}
            />
          </div>
        </div>
      </div>

      <div>
        <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-muted-foreground mb-2">// dimensions</div>
        <div className="grid grid-cols-3 gap-2">
          {dimField("length", "Length", 100, 30000)}
          {dimField("width",  "Depth",  100, 30000)}
          {dimField("height", "Height", 100, 15000)}
        </div>
      </div>

      <div>
        <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-muted-foreground mb-2">// label</div>
        <Input
          data-testid="obstacle-label"
          className="h-8 text-xs rounded-sm font-mono"
          value={ob.label || ""}
          onChange={(e) => patch({ label: e.target.value })}
        />
      </div>

      <Button
        data-testid="obstacle-delete"
        variant="outline"
        className="w-full h-8 rounded-sm font-mono uppercase tracking-widest text-[11px] text-destructive hover:text-destructive hover:bg-destructive/10 border-destructive/40"
        onClick={() => { removeObstacle(ob.id); setSelected(null); }}
      >
        <Trash2 className="w-3.5 h-3.5 mr-1.5" /> Delete
      </Button>
    </div>
  );
}

export default function PropertiesPanel() {
  const objects = useEditor((s) => s.objects);
  const selectedId = useEditor((s) => s.selectedId);
  const obstacles = useEditor((s) => s.floor.obstacles || []);
  const sel = objects.find((o) => o.id === selectedId);
  const selOb = !sel && selectedId ? obstacles.find((o) => o.id === selectedId) : null;

  const header = sel ? "Object" : selOb ? "Obstacle" : "Floor";

  return (
    <aside className="w-72 shrink-0 border-l border-border/60 bg-card flex flex-col min-h-0">
      <div className="px-3 py-2.5 border-b border-border/60">
        <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-muted-foreground">// inspector</div>
        <div className="text-sm font-semibold tracking-tight mt-0.5">{header}</div>
      </div>
      <div className="flex-1 overflow-y-auto scrollbar-thin">
        {sel ? <ObjectProps obj={sel} /> : selOb ? <ObstacleProps ob={selOb} /> : <FloorProps />}
      </div>
      <div className="border-t border-border/60 p-3 font-mono text-[10px] text-muted-foreground flex items-center justify-between">
        <span data-testid="object-count">objects: {objects.length}</span>
        <span>v1.0</span>
      </div>
    </aside>
  );
}
